import React, { useEffect, useMemo, useState } from "react"

import { differenceInMinutes, format, isAfter, isToday } from "date-fns"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { StatusPill } from "@/components/ui/status-pill"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"
import { useInstanceChangeEvents } from "@/hooks/useChangeEvents"
import { useRouteExports } from "@/hooks/useRouteExports"
import {
  useCollectionsConfig,
  useCreateRouteComment,
  useLocations,
  useRouteComments,
  useRoutePlanned,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { HistoryDisclosure, RouteTimeline } from "@/components/collections/ChangeEventList"
import { mergeRouteTimeline } from "@/lib/changeEvents"
import { formatDayMonth } from "@/lib/dateFormats"
import { getRouteAttentionIssues, getRouteStatusDisplay } from "@/lib/statusDisplay"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { Route, RouteComment, Stop } from "@/types/collections"
import {
  calculateDurationMinutes,
  calculateVehicleCapacity,
  findVehicleType,
  priceRoute,
  SEGMENT_LINE_STYLE,
  stopSegmentStates,
} from "@/lib/routeCostCalculation"
import { formatDuration, getDisplayRouteDelayMinutes } from "@/lib/routeDelayDisplay"

import PlanningRouteMapPanel, {
  type MapRouteDisplay,
  type SegmentLineStyle,
} from "../planning/PlanningRouteMapPanel"
import { CommentThread, DetailsPanelFrame, DetailsPanelHeader } from "./DetailsPanel"
import { type AssignmentPicker } from "./AssignmentCombobox"
import RouteActions from "./RouteActions"
import StopDetailsPanel from "./StopDetailsPanel"
import RouteAssignmentSummary from "./route-details/RouteAssignmentSummary"
import RouteCostTab from "./route-details/RouteCostTab"
import RouteStopList from "./route-details/RouteStopList"
import { useRouteAssignment } from "./route-details/useRouteAssignment"
import { useSheriffBooking } from "./route-details/useSheriffBooking"

interface RouteDetailsSheetProps {
  route: Route | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Only avoids mounting a second Mapbox instance; not a layout switch. */
  hostShowsRouteMap?: boolean
  /** DRB assignments are editable only for today. */
  viewDate?: Date
  onCancelled?: (route: Route) => void
  onDuplicated?: (newRouteId: string) => void
}

const RouteDetailsSheet: React.FC<RouteDetailsSheetProps> = ({
  route,
  open,
  onOpenChange,
  hostShowsRouteMap = false,
  viewDate,
  onCancelled,
  onDuplicated,
}) => {
  const { user, can } = useAuth()
  const [comment, setComment] = useState("")
  const [activeTab, setActiveTab] = useState<"stops" | "cost" | "activity">("stops")
  const [historyOpen, setHistoryOpen] = useState(false)

  const isRouteDateToday = useMemo(() => {
    if (!viewDate) return isToday(new Date())
    return isToday(viewDate)
  }, [viewDate])

  const sheriffDriver = useSheriffBooking(route, viewDate, open)

  const { data: dbComments, refetch: refetchComments } = useRouteComments(route?.id)

  // Push attempts come from collections_route_exports, so a failed Track-POD push shows in the
  // history. An empty set sends nothing.
  const routeIds = useMemo(
    () => (route?.id && can.activity ? [route.id] : []),
    [route?.id, can.activity]
  )
  const { data: changeEvents } = useInstanceChangeEvents(routeIds)
  const { data: exportsByRoute } = useRouteExports(routeIds)

  const timeline = useMemo(() => {
    const pushes = route?.id ? Object.values(exportsByRoute?.[route.id] ?? {}) : []
    return mergeRouteTimeline(changeEvents ?? [], pushes)
  }, [changeEvents, exportsByRoute, route?.id])
  const createRouteComment = useCreateRouteComment()

  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()
  const { data: locationsData } = useLocations()

  const { data: plannedRouteData } = useRoutePlanned(
    route?.plannedRouteId && !route?.vehicleType ? route.plannedRouteId : undefined
  )

  const assignment = useRouteAssignment(route, isRouteDateToday && open)

  /**
   * One state for every picker. Radix dismisses the old list on the same click that opens the new
   * one, after the trigger records it, so that late close must not clear the new one.
   */
  const [openPicker, setOpenPicker] = useState<AssignmentPicker | null>(null)
  const pickerOpenSetter = (picker: AssignmentPicker) => (isOpen: boolean) =>
    setOpenPicker((current) => (isOpen ? picker : current === picker ? null : current))

  /**
   * Held by id and read back from `route.stops`, so a reload updates the drill-down and a removed
   * stop closes it.
   */
  const [openStopId, setOpenStopId] = useState<string | null>(null)

  // This component keeps its state while the panel unmounts its contents, so reset on close.
  useEffect(() => {
    if (!open) {
      setOpenPicker(null)
      setOpenStopId(null)
    }
  }, [open])

  useEffect(() => setOpenStopId(null), [route?.id])

  const routeCost = useMemo(() => {
    if (!route || !vehicleTypesData?.data || !configData?.data) return null

    // Recomputed from planned times to handle midnight crossover; stored duration_minutes can be
    // wrong.
    let calculatedDurationMinutes = route.durationMinutes
    if (route.plannedStartTime && route.plannedEndTime) {
      const recalculated = calculateDurationMinutes(route.plannedStartTime, route.plannedEndTime)
      if (recalculated !== null) {
        calculatedDurationMinutes = recalculated
      }
    }

    return priceRoute({
      vehicleTypes: vehicleTypesData.data,
      vehicleTypeRef: route.vehicleType || plannedRouteData?.vehicle_type,
      config: configData.data,
      distanceMiles: route.distanceMiles,
      durationMinutes: calculatedDurationMinutes,
      day: { stops: route.stops, crewSize: route.crewSize, routeKind: route.routeKind },
    })
  }, [route, vehicleTypesData, configData, plannedRouteData])

  const comments: RouteComment[] = React.useMemo(() => {
    if (dbComments && dbComments.length > 0) {
      return dbComments.map((c) => ({
        id: c.id,
        routeId: c.route_instance_id,
        author: c.author_name,
        content: c.content,
        createdAt: new Date(c.created_at),
      }))
    }
    return route?.comments || []
  }, [dbComments, route?.comments])

  // Must be before the early return.
  const vehicleCapacityInfo = useMemo(() => {
    if (!vehicleTypesData?.data || !route) return null

    const hasShipperCollection = (route.stops || []).some(
      (stop) => stop.slotType === "shipper_collection"
    )

    if (!hasShipperCollection) return null

    const vehicleTypeName = route.vehicleType || plannedRouteData?.vehicle_type
    if (!vehicleTypeName) return null

    const vehicleType = findVehicleType(vehicleTypesData.data, vehicleTypeName)

    if (!vehicleType) return null

    const locations = locationsData?.data || []

    return calculateVehicleCapacity(vehicleType, route.stops || [], locations)
  }, [route, vehicleTypesData, plannedRouteData, locationsData])

  const mapRouteDisplay = useMemo((): MapRouteDisplay | null => {
    if (!route) return null
    const slotTypeFromStop = (s: Stop): string => {
      const t = s.slotType ?? s.type
      if (
        typeof t === "string" &&
        [
          "shipper_collection",
          "haulage_collection",
          "haulage_delivery",
          "depot_injection",
          "direct_injection",
          "internal_stop",
          "charging_stop",
        ].includes(t)
      )
        return t
      if (s.type === "collection") return "shipper_collection"
      if (s.type === "delivery") return "haulage_delivery"
      return "depot_injection"
    }
    return {
      code: route.code,
      totalStops: route.totalStops,
      stops: (route.stops || []).map((s) => ({
        id: s.id,
        lat: s.lat,
        lng: s.lng,
        sequence: s.sequence,
        name: s.name,
        address: s.address,
        plannedArrival: s.plannedArrival,
        plannedDeparture: s.plannedDeparture,
        slotType: slotTypeFromStop(s),
      })),
    }
  }, [route])

  const mapSegmentStyles = useMemo(
    (): SegmentLineStyle[] =>
      stopSegmentStates(route?.stops ?? []).map((state) => SEGMENT_LINE_STYLE[state]),
    [route?.stops],
  )

  const handleAddComment = async () => {
    if (!comment.trim() || !route) return

    try {
      await createRouteComment.mutateAsync({
        route_instance_id: route.id,
        author_name: user?.full_name || "Unknown User",
        content: comment.trim(),
      })

      setComment("")
      refetchComments()

      toast.success("Comment added", { description: "Your comment has been saved." })
    } catch (error) {
      console.error("Error adding comment:", error)
      toast.error("Error", { description: "Failed to add comment. Please try again." })
    }
  }

  /** Shared classifier, so the panel and the timeline row can't disagree. */
  const statusDisplay = useMemo(() => (route ? getRouteStatusDisplay(route) : null), [route])

  /** A direct injection goes to a spoke or partner site, so the route never reaches Track-POD. */
  const hasDirectInjection = useMemo(
    () =>
      route?.stops?.some((s) => s.slotType === "direct_injection") ?? false,
    [route],
  )

  const attentionIssues = useMemo(() => {
    if (!route) return []
    const now = new Date()
    const dueTime = route.stops?.length > 0 ? route.stops[0].plannedArrival : route.plannedStartTime
    const minutesToDue = dueTime ? differenceInMinutes(dueTime, now) : 0
    const isPastDue = dueTime ? isAfter(now, dueTime) : false

    return getRouteAttentionIssues(route, now, vehicleCapacityInfo?.totalFillPercentage).map(
      (issue) => {
        let detail = ""
        switch (issue.kind) {
          case "late_start":
            detail = dueTime ? `First stop was due at ${format(dueTime, "HH:mm")}` : ""
            break
          case "no_driver":
          case "no_vehicle":
            detail = isPastDue
              ? "Route is past due and still unassigned"
              : `Route is due in ${formatDuration(minutesToDue)}`
            break
          case "running_late":
            detail = `${formatDuration(getDisplayRouteDelayMinutes(route))} behind schedule`
            break
          case "over_capacity":
            detail = "Load exceeds the vehicle's capacity"
            break
        }
        return { ...issue, detail }
      }
    )
  }, [route, vehicleCapacityInfo])

  if (!route || !open) return null

  /** Read back each render so the drill-down tracks the row it came from. */
  const openStop = openStopId ? (route.stops.find((s) => s.id === openStopId) ?? null) : null

  const headerContent = (
    <DetailsPanelHeader
      title={route.code}
      meta={
        <>
          {statusDisplay && (
            <StatusPill tone={statusDisplay.tone} label={statusDisplay.detailLabel} withDot />
          )}
          {route.isAdhoc && (
            <Badge
              variant="outline"
              className="border-palette-jungleGreen200 px-1.5 py-0 text-[10px] text-palette-jungleGreen500"
            >
              Ad-hoc
            </Badge>
          )}
          {hasDirectInjection && (
            <Badge
              variant="outline"
              className="border-palette-jungleGreen200 px-1.5 py-0 text-[10px] text-palette-jungleGreen500"
            >
              Not sent to Track-POD
            </Badge>
          )}
        </>
      }
      subtitle={
        <>
          {route.driverName?.trim() || "No driver"} · {route.vehicleReg?.trim() || "No vehicle"} ·{" "}
          {formatDayMonth(route.plannedStartTime)}
        </>
      }
      closeLabel="Close route details"
      onClose={() => onOpenChange(false)}
      actions={
        can.editRoutes ? (
          <RouteActions
            route={route}
            dateStr={format(viewDate ?? route.plannedStartTime, "yyyy-MM-dd")}
            onCancelled={onCancelled}
            onDuplicated={onDuplicated}
          />
        ) : undefined
      }
    />
  )

  const summaryContent = (
    <div className="flex-shrink-0 space-y-3 px-5 py-4">
      {attentionIssues.length > 0 && (
        <div
          className={cn(
            "overflow-hidden rounded-lg border",
            toneClasses("danger").border,
            toneClasses("danger").surface
          )}
        >
          <div className="divide-y divide-red-100">
            {attentionIssues.map((issue) => (
              <div key={issue.kind} className="flex items-start gap-2.5 px-3 py-2">
                <span
                  className={cn(
                    "mt-1.5 h-1.5 w-1.5 flex-none rounded-full",
                    toneClasses(issue.tone).dot
                  )}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  {/* The card is always the danger tint, so the label uses its tone's on-tint
                      ink for contrast. */}
                  <div className={cn("text-xs font-medium", toneClasses(issue.tone).onSurface)}>
                    {issue.label}
                  </div>
                  {issue.detail && (
                    <div className="text-[11px] text-palette-jungleGreen600">{issue.detail}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <RouteAssignmentSummary
        route={route}
        isRouteDateToday={isRouteDateToday}
        canAssignVehicles={can.assignVehicles}
        assignment={assignment}
        sheriffDriver={sheriffDriver}
        openPicker={openPicker}
        onPickerOpenChange={pickerOpenSetter}
      />
    </div>
  )

  const stopsTab = (
    <div className="space-y-4 px-5 py-4">
      {!hostShowsRouteMap &&
        mapRouteDisplay &&
        mapRouteDisplay.stops.some((s) => s.lat != null && s.lng != null) && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-palette-jungleGreen900">Route map</h3>
            <PlanningRouteMapPanel
              route={mapRouteDisplay}
              segmentStyles={mapSegmentStyles}
              onClose={() => {}}
              inline
              embedded
              driverLocation={
                route.currentLat != null && route.currentLng != null
                  ? { lat: route.currentLat, lng: route.currentLng }
                  : undefined
              }
              driverName={route.driverName}
            />
          </div>
        )}

      <RouteStopList
        stops={route.stops}
        completedStops={route.completedStops}
        totalStops={route.totalStops}
        onOpenStop={setOpenStopId}
      />
    </div>
  )

  const activityTab = (
    <div className="space-y-5 px-5 py-4">
      <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000">
        <HistoryDisclosure
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          count={timeline.length}
          headerClassName="px-3 py-2.5"
          bodyClassName="border-t border-palette-jungleGreen200 px-3 py-3"
        >
          <RouteTimeline items={timeline} />
        </HistoryDisclosure>
      </div>

      <CommentThread
        title="Route Comments"
        comments={comments}
        value={comment}
        onChange={setComment}
        onSubmit={handleAddComment}
        isPending={createRouteComment.isPending}
      />
    </div>
  )

  const mainContent = (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as typeof activeTab)}
      className="flex min-h-0 flex-1 flex-col"
    >
      {(can.costs || can.activity) && (
        <div className="flex-shrink-0 px-5">
          <TabsList className="h-8 w-full justify-start bg-palette-jungleGreen100 p-1">
            <TabsTrigger value="stops" className="px-3 py-1 text-xs">
              Stops
              <span className="ml-1.5 text-palette-jungleGreen500">{route.stops.length}</span>
            </TabsTrigger>
            {can.costs && (
              <TabsTrigger value="cost" className="px-3 py-1 text-xs">
                Cost &amp; load
              </TabsTrigger>
            )}
            {can.activity && (
              <TabsTrigger value="activity" className="px-3 py-1 text-xs">
                Activity
                {comments.length > 0 && (
                  <span className="ml-1.5 text-palette-jungleGreen500">{comments.length}</span>
                )}
              </TabsTrigger>
            )}
          </TabsList>
        </div>
      )}

      <TabsContent value="stops" className="mt-0 min-h-0 flex-1">
        <ScrollArea className="h-full">{stopsTab}</ScrollArea>
      </TabsContent>
      {can.costs && (
        <TabsContent value="cost" className="mt-0 min-h-0 flex-1">
          <ScrollArea className="h-full">
            <RouteCostTab
              route={route}
              routeCost={routeCost}
              plannedVehicleType={plannedRouteData?.vehicle_type}
              vehicleCapacityInfo={vehicleCapacityInfo}
            />
          </ScrollArea>
        </TabsContent>
      )}
      {can.activity && (
        <TabsContent value="activity" className="mt-0 min-h-0 flex-1">
          <ScrollArea className="h-full">{activityTab}</ScrollArea>
        </TabsContent>
      )}
    </Tabs>
  )

  return (
    <>
      <DetailsPanelFrame
        /* Neither the open picker nor the drilled-in stop is a Radix dialog, so the frame can't
           see them; close that layer first. */
        onDismiss={() => {
          if (openPicker) {
            setOpenPicker(null)
            return
          }
          if (openStopId) {
            setOpenStopId(null)
            return
          }
          onOpenChange(false)
        }}
      >
        {headerContent}
        {summaryContent}
        {mainContent}
      </DetailsPanelFrame>

      {/* Same z-index as the route frame, so DOM order puts it on top. The only way into a stop
          from the Map tab, or for an unlocked route. */}
      {openStop && (
        <StopDetailsPanel
          stop={openStop}
          route={route}
          open
          onOpenChange={(next) => {
            if (!next) setOpenStopId(null)
          }}
        />
      )}
    </>
  )
}

export default RouteDetailsSheet
